/**
 * Return ellipsis of a given string
 * @param {string} text
 * @param {number} size
 */

import dicomParser from 'dicom-parser';
import JSZip from 'jszip';
const ellipsis = (text, size) => {
  return `${text.split(' ').slice(0, size).join(' ')}...`;
};

const idGenerator = (events, length = 1) => {
  const arrayData = [];
  events.map((data) => {
    return arrayData.push(parseInt(data.id, 10));
  });
  const number = (Math.max(...arrayData) + 1).toString();
  return number.length < length ? `${'0'.repeat(length - number.length)}${number}` : number;
};

const dateObjToString = (date, format) => {
  const year = date?.getFullYear()?.toString();
  let month = ((date?.getMonth() || 0) + 1)?.toString();
  let day = String(date?.getDate());

  if (year === 'NaN' || month === 'NaN' || day === 'NaN') return null;

  month = month?.length === 1 ? `0${month}` : month;
  day = day?.length === 1 ? `0${day}` : day;

  return format
    .replace(/YYYY|yyyy/g, year)
    .replace(/MM|mm/g, month)
    .replace(/DD|dd/g, day);
};

const blobToFile = (theBlob, fileName) => {
  //A Blob() is almost a File() - it's just missing the two properties below which we will add
  let file = new File([theBlob], fileName, { lastModified: new Date(), type: theBlob.type, size: theBlob.size });

  return file;
};

const blobToDCMFile = (theBlob, fileName) => {
  return new File([theBlob], fileName, { type: 'application/dicom' });
};

const getFileExtFromBase64 = (base64Data) => {
  return base64Data.substring('data:image/'.length, base64Data.indexOf(';base64'));
};

const validateDICOMFile = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    // Read the file as an ArrayBuffer
    fileReader.readAsArrayBuffer(file);

    // Handle file read completion
    fileReader.onload = () => {
      const arrayBuffer = fileReader.result;
      const dataView = new DataView(arrayBuffer);

      // Check the file signature
      const fileSignature = dataView.getUint32(128, false).toString(16);
      if (fileSignature === '42494d44') {
        resolve('Valid DICOM file');
      } else {
        reject(new Error('Invalid DICOM file'));
      }
    };

    // Handle file read error
    fileReader.onerror = () => {
      reject(new Error('Error reading file'));
    };
  });
};

function getFileExtension(filename) {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
}

const readFileAsBinary = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => resolve(reader.result);

    reader.onerror = (error) => reject(error);
  });
};

function haveDistinctParentPatients(data) {
  for (let i = 0; i < data.length; i++) {
    for (let j = i + 1; j < data.length; j++) {
      if (data[i].ParentPatient !== data[j].ParentPatient) {
        return true;
      }
    }
  }
  return false;
}

function getDistinctParentPatients(data) {
  const distinctParentPatients = new Set();
  const result = [];

  for (const item of data) {
    if (!distinctParentPatients.has(item.ParentPatient)) {
      distinctParentPatients.add(item.ParentPatient);
      result.push(item);
    }
  }

  return result;
}

function getDistinctParentStudy(data) {
  const distinctParentStudies = new Set();
  const result = [];

  for (const item of data) {
    if (!distinctParentStudies.has(item.ParentStudy)) {
      distinctParentStudies.add(item.ParentStudy);
      result.push(item);
    }
  }

  return result;
}

function extractInstanceDetails(response) {
  const studies = new Set();

  function traverse(obj) {
    if (Array.isArray(obj)) {
      obj.forEach((item) => {
        traverse(item);
      });
    } else if (typeof obj === 'object' && obj !== null) {
      if (obj.ParentStudy) {
        studies.add(obj.ParentStudy);
      } else {
        Object.values(obj).forEach((value) => {
          if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
            traverse(value);
          }
        });
      }
    } else if (typeof obj === 'string') {
      studies.add(obj);
    }
  }

  traverse(response);

  return Array.from(studies);
}

function convertFromBytes(bytes) {
  if (bytes === 0 || bytes === null || bytes === undefined) return '0 Bytes';
  // const k = 1024;
  const k = 1000;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (bytes / Math.pow(k, i)).toFixed(i > 0 ? 2 : 0) + ' ' + sizes[i];
}

const parseDicomFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target.result;
      try {
        const dataSet = dicomParser.parseDicom(new Uint8Array(arrayBuffer));
        const desiredTags = {
          x00100020: 'PatientID',
          x00100010: 'PatientName',
          x00100030: 'PatientBirthDate',
          x00100040: 'PatientSex',
          x00380300: 'PatientHistory',
          x00104000: 'PatientComments',
          x001021b0: 'AdditionalPatientHistory',
          x00080060: 'Modality',
          x00081030: 'StudyDescription',
          x00080020: 'StudyDate',
          x00080030: 'StudyTime',
          x00120010: 'PatientMedicalHistory',
          x0020000d: 'StudyInstanceUID',
          x00080050: 'AccessionNumber',
          x00080080: 'InstitutionName',
          x00200010: 'StudyID',
          x00080090: 'ReferringPhysicianName',
        };

        const patientDetails = {};

        for (const prop in desiredTags) {
          const tagValue = dataSet.string(prop);
          if (tagValue) {
            patientDetails[desiredTags[prop]] = tagValue;
          }
        }

        resolve(patientDetails);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};

const handleZipFile = async (file) => {
  const jszip = new JSZip();
  const zip = await jszip.loadAsync(file);
  const dicomFiles = [];

  for (const filename of Object.keys(zip.files)) {
    try {
      const fileData = await zip.files[filename].async('arraybuffer');

      const dataSet = dicomParser.parseDicom(new Uint8Array(fileData));

      // Extract desired tags from the DICOM data
      const desiredTags = {
        x00100020: 'PatientID',
        x00100010: 'PatientName',
        x00100030: 'PatientBirthDate',
        x00100040: 'PatientSex',
        x00380300: 'PatientHistory',
        x00104000: 'PatientComments',
        x001021b0: 'AdditionalPatientHistory',
        x00080060: 'Modality',
        x00081030: 'StudyDescription',
        x00080020: 'StudyDate',
        x00080030: 'StudyTime',
        x00120010: 'PatientMedicalHistory',
        x0020000d: 'StudyInstanceUID',
        x00080050: 'AccessionNumber',
        x00080080: 'InstitutionName',
        x00200010: 'StudyID',
        x00080090: 'ReferringPhysicianName',
      };

      const patientDetails = {};

      for (const prop in desiredTags) {
        const tagValue = dataSet.string(prop);
        if (tagValue) {
          patientDetails[desiredTags[prop]] = tagValue;
        }
      }
      dicomFiles.push(patientDetails);
      // dicomFiles.push(dataSet);
    } catch (error) {
      console.log(error);
    }
  }
  return dicomFiles;
};

const getDataSet = async (file) => {
  if (file.name.endsWith('.zip')) {
    return await handleZipFile(file);
  } else if (file.name.endsWith('.dcm')) {
    return [await parseDicomFile(file)];
  } else {
    throw new Error('Unsupported file type');
  }
};

const getDistinctStudiesFromDataset = (studies) => {
  const uniqueStudies = [];
  const seen = new Set();

  for (const study of studies) {
    if (study.StudyInstanceUID && !seen.has(study.StudyInstanceUID)) {
      uniqueStudies.push(study);
      seen.add(study.StudyInstanceUID);
    }
  }
  return uniqueStudies;
};

function convertDateToDDMMMYYYY(dateStr) {
  if (!dateStr || dateStr === undefined || dateStr === 'undefined') return '';

  // Remove dashes if present (so YYYY-MM-DD → YYYYMMDD)
  const cleaned = dateStr.replace(/-/g, '');

  if (cleaned.length !== 8) {
    throw new Error(`Invalid date format: ${dateStr}`);
  }

  const year = cleaned.substring(0, 4);
  const month = cleaned.substring(4, 6);
  const day = cleaned.substring(6, 8);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const monthName = months[parseInt(month, 10) - 1];

  return `${day} ${monthName} ${year}`;
}

// function convertTimeToHHMM(timeStr) {
//   if (!timeStr || timeStr === undefined || timeStr === 'undefined') return '';
//   // Expecting timeStr in format HHMMSS.ffffff
//   const hours = timeStr.substring(0, 2);
//   const minutes = timeStr.substring(2, 4);
//   // Skipping seconds and fractions for simplicity

//   return `${hours}:${minutes}`;
// }

function convertTimeToHHMM(timeStr) {
  if (!timeStr) return '--:--';

  const s = String(timeStr).trim();
  if (!s) return '--:--';

  const parts = s.split('.');
  let left = parts[0] || '';
  left = left.replace(/\D/g, '');

  if (left.length < 3) return '--:--';

  if (left.length === 5) {
    left = left.padStart(6, '0');
  } else if (left.length < 6) {
    left = left.padStart(6, '0');
  } else if (left.length > 6) {
    left = left.substring(0, 6);
  }

  const hours = parseInt(left.substring(0, 2), 10);
  const minutes = parseInt(left.substring(2, 4), 10);
  const seconds = parseInt(left.substring(4, 6), 10);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    Number.isNaN(seconds) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  ) {
    return '--:--';
  }

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');

  return `${hh}:${mm}`;
}

const getTextContent = (element) => {
  if (typeof element === 'string') return element;
  if (element && element.props && element.props.children) {
    if (Array.isArray(element.props.children)) {
      return element.props.children.join('');
    }
    return element.props.children;
  }
  return '';
};

function getLastDateOfCurrentMonth() {
  const today = new Date();
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  // Function to get the correct suffix for the day
  const getDayWithSuffix = (day) => {
    if (day > 3 && day < 21) return day + 'th'; // covers 11th to 20th
    switch (day % 10) {
      case 1:
        return day + 'st';
      case 2:
        return day + 'nd';
      case 3:
        return day + 'rd';
      default:
        return day + 'th';
    }
  };

  // Array of month names
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Format the date
  return `${getDayWithSuffix(lastDayOfMonth.getDate())} ${
    months[lastDayOfMonth.getMonth()]
  }, ${lastDayOfMonth.getFullYear()}`;
}

function formatSizeInBytes(sizeInBytes) {
  const ONE_GB = 1073741824; // 1024^3
  const ONE_MB = 1048576; // 1024^2

  if (sizeInBytes >= ONE_GB) {
    return `${(sizeInBytes / ONE_GB).toFixed(2)} GB`;
  } else if (sizeInBytes >= ONE_MB) {
    return `${(sizeInBytes / ONE_MB).toFixed(0)} MB`;
  } else {
    return `${sizeInBytes} Bytes`;
  }
}

function extractDimensions(url) {
  // Supports both naming styles:
  // - *_150x130.png
  // - *letterhead150x130.png / *letterhead150x130.jpg
  const match = url.match(/(?:_|letterhead)(\d+(\.\d+)?)x(\d+(\.\d+)?)(?:\.(png|jpg|jpeg))$/i);
  if (!match) return null;

  const top = parseFloat(match[1]);
  const bottom = parseFloat(match[3]);

  return { top, bottom };
}

function determineViewersFromMetadata(modality, seriesList) {
  const viewers = new Set(['basic']);

  // Add modality-specific viewers
  // if (['CT', 'MR', 'PT'].includes(modality)) {
  //   viewers.add('mpr');
  // }

  if (['CT', 'MR', 'PT', 'DX', 'XA', 'US'].includes(modality)) {
    viewers.add('segmentation');
  }

  // Check each series for specific features
  seriesList.forEach((series) => {
    const tags = series.MainDicomTags || {};
    const description = (tags.SeriesDescription || '').toUpperCase();
    const protocol = (tags.ProtocolName || '').toUpperCase();

    // Segmentation series
    if (tags.Modality === 'SEG') {
      viewers.add('segmentation');
    }

    // Microscopy
    if (tags.Modality === 'SM') {
      viewers.add('microscopy');
    }

    // 4D data
    if (
      description.includes('4D') ||
      protocol.includes('4D') ||
      description.includes('DYNAMIC') ||
      description.includes('TEMPORAL') ||
      tags?.Modality?.includes('PT')
    ) {
      viewers.add('preclinical-4d');
    }

    // US Pleura
    if (modality === 'US' && (description.includes('PLEURA') || description.includes('B-LINE'))) {
      viewers.add('us-pleura');
    }

    // TMTV
    if (
      ((modality === 'PT' || modality === 'CT') &&
        (description.includes('TMTV') || description.includes('METABOLIC'))) ||
      tags?.Modality?.includes('PT')
    ) {
      viewers.add('tmtv');
    }
  });

  return Array.from(viewers);
}

export {
  ellipsis,
  idGenerator,
  dateObjToString,
  blobToFile,
  getFileExtFromBase64,
  blobToDCMFile,
  validateDICOMFile,
  getFileExtension,
  readFileAsBinary,
  haveDistinctParentPatients,
  extractInstanceDetails,
  getDistinctParentPatients,
  getDistinctParentStudy,
  convertFromBytes,
  getDataSet,
  getDistinctStudiesFromDataset,
  convertDateToDDMMMYYYY,
  convertTimeToHHMM,
  getTextContent,
  getLastDateOfCurrentMonth,
  formatSizeInBytes,
  extractDimensions,
  determineViewersFromMetadata,
};
